import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Plus, Phone, Mail, MapPin, Search, ChevronDown, ChevronUp, 
  Trash2, Edit, Check, X, Home, LayoutDashboard, Package, 
  MessageSquare, LogOut, Users, Clock, CheckCircle, XCircle,
  AlertCircle, ArrowLeft, Eye, PlusCircle, Settings, Download, Upload,
  BarChart3, TrendingUp, PieChart, Database, RefreshCw, FolderPlus, Layers, Pencil
} from "lucide-react";
import { 
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default equipment types (fallback)
const DEFAULT_EQUIPMENT_TYPES = [
  "Wheelchair",
  "Walker",
  "Knee Scooter",
  "Crutches",
  "Hospital Bed",
  "Shower Chair",
  "Commode",
  "Other"
];

const AVAILABILITY_STATUSES = ["Available", "On Loan", "Unavailable"];
const REQUEST_STATUSES = ["Pending", "Approved", "Rejected", "Completed"];

// Hook to fetch equipment types
const useEquipmentTypes = () => {
  const [equipmentTypes, setEquipmentTypes] = useState([...DEFAULT_EQUIPMENT_TYPES].sort());
  const [loading, setLoading] = useState(true);

  const fetchTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/equipment-types`);
      const types = response.data.map(t => t.name).sort((a, b) => a.localeCompare(b));
      setEquipmentTypes(types.length > 0 ? types : [...DEFAULT_EQUIPMENT_TYPES].sort());
    } catch (error) {
      console.error("Error fetching equipment types:", error);
      setEquipmentTypes([...DEFAULT_EQUIPMENT_TYPES].sort());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  return { equipmentTypes, loading, refetch: fetchTypes };
};

// Navigation Links for Regency Cares
const navLinks = [
  { name: "HOME PAGE", href: "https://regencycares.com" },
  { name: "SERVICES", href: "https://regencycares.com/services" },
  { name: "EQUILIBRIUM", href: "https://regencycares.com/equilibrium" },
  { name: "REQUEST CARE", href: "https://regencycares.com/request-care" },
  { name: "MEDICAL LIBRARY", href: "#", active: true },
];

// Header Component - Matching regencycares.com format
const Header = ({ onOfferClick, onAdminClick, isAdmin, onLogout, onDashboardClick }) => {
  return (
    <header className="sticky top-0 z-50" data-testid="header">
      {/* Top Title Bar - Light gray */}
      <div className="bg-gray-100 py-4 text-center border-b border-gray-200">
        <h1 className="text-3xl md:text-4xl font-light text-gray-600 tracking-wide">
          Regency Cares: <span className="italic">How can we care for you?</span>
        </h1>
      </div>
      
      {/* Navigation Bar - Dark gray matching regencycares.com */}
      <div className="bg-gray-700 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-12">
            {/* Navigation Links */}
            <nav className="flex items-center space-x-6">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className={`text-sm font-medium tracking-wide hover:text-pink-300 transition-colors ${
                    link.active ? 'text-pink-300 border-b-2 border-pink-300' : 'text-white'
                  }`}
                >
                  {link.name}
                </a>
              ))}
            </nav>
            
            {/* Right side buttons */}
            <div className="flex items-center gap-2">
              {isAdmin ? (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={onDashboardClick}
                    className="text-white hover:text-pink-300 hover:bg-gray-600 h-8 text-xs"
                    data-testid="admin-dashboard-btn"
                  >
                    <LayoutDashboard className="w-3 h-3 mr-1" />
                    Dashboard
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={onLogout}
                    className="text-white hover:text-pink-300 hover:bg-gray-600 h-8 text-xs"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-3 h-3 mr-1" />
                    Logout
                  </Button>
                </>
              ) : (
                <Button 
                  variant="ghost" 
                  onClick={onAdminClick}
                  className="text-white hover:text-pink-300 hover:bg-gray-600 h-8 text-xs"
                  data-testid="admin-login-btn"
                >
                  Admin
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Search and Filter Component
const SearchFilter = ({ search, setSearch, category, setCategory, categories }) => {
  return (
    <Card className="mb-6" data-testid="search-filter">
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-700 mb-2 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search equipment or provider..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-gray-700 mb-2 block">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Categories</SelectItem>
                {Object.keys(categories).sort().map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Equipment Row Component
const EquipmentRow = ({ equipment, onRequestClick }) => {
  const [expanded, setExpanded] = useState(false);
  const pendingRequests = equipment.requests?.filter(r => r.status === "Pending") || [];

  const getStatusColor = (status) => {
    switch (status) {
      case "Available":
        return "bg-green-100 text-green-800 border-green-200";
      case "On Loan":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Unavailable":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <>
      <TableRow className="hover:bg-gray-50" data-testid={`equipment-row-${equipment.id}`}>
        <TableCell>
          {/* Category stacked above Subcategory */}
          <div className="font-semibold text-gray-900">{equipment.category || "Uncategorized"}</div>
          <div className="text-sm text-blue-600 mt-1">{equipment.subcategory || equipment.equipment_type || "N/A"}</div>
        </TableCell>
        <TableCell>
          <div className="text-gray-700">{equipment.description}</div>
          {equipment.special_notes && (
            <div className="text-sm text-gray-500 italic mt-1">Note: {equipment.special_notes}</div>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setExpanded(!expanded)}
              data-testid={`more-btn-${equipment.id}`}
            >
              {expanded ? "Less" : "More"}
              {expanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="bg-gray-50" data-testid={`equipment-details-${equipment.id}`}>
          <TableCell colSpan={3}>
            <div className="p-4">
              <h4 className="font-semibold text-gray-900 mb-3 border-b pb-2">Contact Information</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <span className="text-sm text-gray-500 block">Provider</span>
                  <span className="font-medium text-gray-900">{equipment.provider_name}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">Address</span>
                  <span className="font-medium text-gray-900">{equipment.full_address}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">Phone</span>
                  <a href={`tel:${equipment.phone_number}`} className="text-blue-600 hover:underline">
                    {equipment.phone_number}
                  </a>
                </div>
                <div>
                  <span className="text-sm text-gray-500 block">Email</span>
                  <a href={`mailto:${equipment.email}`} className="text-blue-600 hover:underline">
                    {equipment.email}
                  </a>
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// Equipment Table Component
const EquipmentTable = ({ equipment, onRequestClick }) => {
  return (
    <Card data-testid="equipment-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50">
            {/* Status column hidden */}
            <TableHead className="w-[250px]">Category / Subcategory</TableHead>
            <TableHead>Equipment Description</TableHead>
            <TableHead className="w-[150px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {equipment.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                No equipment found. Be the first to share!
              </TableCell>
            </TableRow>
          ) : (
            equipment.map((item) => (
              <EquipmentRow 
                key={item.id} 
                equipment={item} 
                onRequestClick={onRequestClick}
              />
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
};

// Request Equipment Modal
const RequestModal = ({ isOpen, onClose, equipment, onSubmit }) => {
  const [formData, setFormData] = useState({
    requester_name: "",
    requester_email: "",
    requester_phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(equipment.id, formData);
      setFormData({ requester_name: "", requester_email: "", requester_phone: "", message: "" });
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="request-modal">
        <DialogHeader>
          <DialogTitle>Request Equipment</DialogTitle>
          <DialogDescription>
            Request to borrow: {equipment?.equipment_type} from {equipment?.provider_name}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="requester_name">Your Name *</Label>
            <Input
              id="requester_name"
              value={formData.requester_name}
              onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })}
              required
              data-testid="request-name-input"
            />
          </div>
          <div>
            <Label htmlFor="requester_email">Email *</Label>
            <Input
              id="requester_email"
              type="email"
              value={formData.requester_email}
              onChange={(e) => setFormData({ ...formData, requester_email: e.target.value })}
              required
              data-testid="request-email-input"
            />
          </div>
          <div>
            <Label htmlFor="requester_phone">Phone Number *</Label>
            <Input
              id="requester_phone"
              value={formData.requester_phone}
              onChange={(e) => setFormData({ ...formData, requester_phone: e.target.value })}
              required
              data-testid="request-phone-input"
            />
          </div>
          <div>
            <Label htmlFor="message">Message (Optional)</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Any special requirements or questions?"
              data-testid="request-message-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="submit-request-btn">
              {loading ? "Sending..." : "Send Request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Admin Login Modal
const AdminLoginModal = ({ isOpen, onClose, onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const success = await onLogin(password);
      if (success) {
        setPassword("");
        onClose();
      } else {
        setError("Invalid password");
      }
    } catch (err) {
      setError("Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="admin-login-modal">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl">Admin Login</DialogTitle>
          <DialogDescription className="text-center">
            Enter your password to access the admin dashboard
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password-input"
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
          <div className="flex flex-col gap-2">
            <Button type="submit" disabled={loading} className="w-full" data-testid="admin-login-submit">
              {loading ? "Logging in..." : "Login"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="w-full">
              Back to Home
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, subtext }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    green: "bg-green-50 text-green-600 border-green-200",
    yellow: "bg-yellow-50 text-yellow-600 border-yellow-200",
    red: "bg-red-50 text-red-600 border-red-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200"
  };

  return (
    <Card className={`border ${colorClasses[color]}`} data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Admin Dashboard Page
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ 
    total_equipment: 0, 
    available_equipment: 0, 
    total_requests: 0, 
    pending_requests: 0,
    total_visitors: 0,
    unique_visitors: 0,
    admin_count: 1
  });
  const [equipment, setEquipment] = useState([]);
  const [requests, setRequests] = useState([]);
  const [equipmentTypesList, setEquipmentTypesList] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({
    equipmentByCategory: [],
    requestsByStatus: [],
    popularEquipment: [],
    visitsOverTime: [],
    requestsOverTime: [],
    equipmentByStatus: []
  });
  const [newTypeName, setNewTypeName] = useState("");
  const [newAdminUsername, setNewAdminUsername] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Category management state
  const [adminCategories, setAdminCategories] = useState({});
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newSubcategoryName, setNewSubcategoryName] = useState("");
  const [selectedCategoryForSub, setSelectedCategoryForSub] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editSubcategoryName, setEditSubcategoryName] = useState("");

  // Chart colors
  const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, equipmentRes, requestsRes, typesRes, adminUsersRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/equipment?availability=All`),
        axios.get(`${API}/requests`),
        axios.get(`${API}/equipment-types`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setEquipment(equipmentRes.data);
      setRequests(requestsRes.data);
      setEquipmentTypesList(typesRes.data);
      setAdminUsers(adminUsersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [catRes, statusRes, popularRes, visitsRes, reqTimeRes, eqStatusRes] = await Promise.all([
        axios.get(`${API}/analytics/equipment-by-category`),
        axios.get(`${API}/analytics/requests-by-status`),
        axios.get(`${API}/analytics/popular-equipment`),
        axios.get(`${API}/analytics/visits-over-time`),
        axios.get(`${API}/analytics/requests-over-time`),
        axios.get(`${API}/analytics/equipment-by-status`)
      ]);
      setAnalyticsData({
        equipmentByCategory: catRes.data,
        requestsByStatus: statusRes.data,
        popularEquipment: popularRes.data,
        visitsOverTime: visitsRes.data,
        requestsOverTime: reqTimeRes.data,
        equipmentByStatus: eqStatusRes.data
      });
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  }, []);

  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin");
    if (isAdmin !== "true") {
      navigate("/");
      return;
    }
    fetchData();
    fetchAnalytics();
    fetchAdminCategories();
  }, [fetchData, fetchAnalytics, navigate]);

  const fetchAdminCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setAdminCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${API}/categories`, { name: newCategoryName.trim(), subcategories: [] });
      toast.success(`Category '${newCategoryName.trim()}' added`);
      setNewCategoryName("");
      fetchAdminCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add category");
    }
  };

  const handleRenameCategory = async (oldName) => {
    if (!editCategoryName.trim() || editCategoryName.trim() === oldName) {
      setEditingCategory(null);
      return;
    }
    try {
      await axios.put(`${API}/categories/${encodeURIComponent(oldName)}`, { new_name: editCategoryName.trim() });
      toast.success(`Category renamed to '${editCategoryName.trim()}'`);
      setEditingCategory(null);
      setEditCategoryName("");
      fetchAdminCategories();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rename category");
    }
  };

  const handleDeleteCategory = async (name) => {
    if (!window.confirm(`Delete category '${name}' and all its subcategories?`)) return;
    try {
      await axios.delete(`${API}/categories/${encodeURIComponent(name)}`);
      toast.success(`Category '${name}' deleted`);
      fetchAdminCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete category");
    }
  };

  const handleAddSubcategory = async () => {
    if (!newSubcategoryName.trim() || !selectedCategoryForSub) return;
    try {
      await axios.post(`${API}/categories/${encodeURIComponent(selectedCategoryForSub)}/subcategories`, { name: newSubcategoryName.trim() });
      toast.success(`Subcategory '${newSubcategoryName.trim()}' added`);
      setNewSubcategoryName("");
      fetchAdminCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add subcategory");
    }
  };

  const handleRenameSubcategory = async (categoryName, oldName) => {
    if (!editSubcategoryName.trim() || editSubcategoryName.trim() === oldName) {
      setEditingSubcategory(null);
      return;
    }
    try {
      await axios.put(`${API}/categories/${encodeURIComponent(categoryName)}/subcategories`, {
        old_name: oldName,
        new_name: editSubcategoryName.trim()
      });
      toast.success(`Subcategory renamed to '${editSubcategoryName.trim()}'`);
      setEditingSubcategory(null);
      setEditSubcategoryName("");
      fetchAdminCategories();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to rename subcategory");
    }
  };

  const handleDeleteSubcategory = async (categoryName, subcategoryName) => {
    if (!window.confirm(`Delete subcategory '${subcategoryName}'?`)) return;
    try {
      await axios.delete(`${API}/categories/${encodeURIComponent(categoryName)}/subcategories/${encodeURIComponent(subcategoryName)}`);
      toast.success(`Subcategory '${subcategoryName}' deleted`);
      fetchAdminCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete subcategory");
    }
  };

  const handleUpdateRequestStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/requests/${requestId}/status?status=${status}`);
      toast.success(`Request ${status.toLowerCase()}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update request status");
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this request?")) return;
    try {
      await axios.delete(`${API}/requests/${requestId}`);
      toast.success("Request deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm("Are you sure you want to delete this equipment?")) return;
    try {
      await axios.delete(`${API}/equipment/${equipmentId}`);
      toast.success("Equipment deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete equipment");
    }
  };

  const handleAddEquipmentType = async () => {
    if (!newTypeName.trim()) {
      toast.error("Please enter a type name");
      return;
    }
    try {
      await axios.post(`${API}/equipment-types`, { name: newTypeName.trim() });
      toast.success("Equipment type added");
      setNewTypeName("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add equipment type");
    }
  };

  const handleDeleteEquipmentType = async (typeId, typeName) => {
    if (!window.confirm(`Are you sure you want to delete "${typeName}"?`)) return;
    try {
      await axios.delete(`${API}/equipment-types/${typeId}`);
      toast.success("Equipment type deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete equipment type");
    }
  };

  const handleEditEquipment = (eq) => {
    navigate("/offer", { state: { equipment: eq } });
  };

  const handleUpdateEquipmentStatus = async (equipmentId, status) => {
    try {
      await axios.put(`${API}/equipment/${equipmentId}`, { availability_status: status });
      toast.success("Equipment status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update equipment status");
    }
  };

  const handleAddAdminUser = async () => {
    if (!newAdminUsername.trim() || !newAdminPassword.trim()) {
      toast.error("Please enter username and password");
      return;
    }
    try {
      await axios.post(`${API}/admin/users`, { 
        username: newAdminUsername.trim(), 
        password: newAdminPassword.trim() 
      });
      toast.success("Admin user added");
      setNewAdminUsername("");
      setNewAdminPassword("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add admin user");
    }
  };

  const handleDeleteAdminUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete admin "${username}"?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Admin user deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete admin user");
    }
  };

  // CSV Export/Import handlers
  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/equipment/export/csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `equipment_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Equipment list exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export equipment list");
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/equipment/import/csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { imported, updated, errors } = response.data;
      
      if (imported > 0 || updated > 0) {
        toast.success(`Imported: ${imported} new, Updated: ${updated} existing`);
      }
      
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} rows had errors. Check console for details.`);
        console.error("Import errors:", errors);
      }
      
      fetchData(); // Refresh the list
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import equipment list");
    }
    
    // Reset file input
    event.target.value = '';
  };

  // Database backup export
  const [backupStatus, setBackupStatus] = useState({ available: false, filename: null, created_at: null });
  const [backupData, setBackupData] = useState(null);
  const [backupDownloadUrl, setBackupDownloadUrl] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);

  // Check backup status on load
  useEffect(() => {
    const checkBackupStatus = async () => {
      try {
        const response = await axios.get(`${API}/admin/backup-status`);
        setBackupStatus(response.data);
      } catch (error) {
        console.log("No backup available yet");
      }
    };
    checkBackupStatus();
  }, []);

  const handleCreateBackup = async () => {
    setBackupLoading(true);
    try {
      // First create backup on server
      const createResponse = await axios.post(`${API}/admin/create-backup`);
      
      // Then fetch the actual backup data
      const dataResponse = await axios.get(`${API}/admin/download-backup`);
      
      // Create download URL from the data
      const jsonString = JSON.stringify(dataResponse.data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Clean up old URL if exists
      if (backupDownloadUrl) {
        URL.revokeObjectURL(backupDownloadUrl);
      }
      
      setBackupData(dataResponse.data);
      setBackupDownloadUrl(url);
      setBackupStatus({
        available: true,
        filename: createResponse.data.filename,
        created_at: createResponse.data.created_at
      });
      toast.success(`Backup ready! Click the download link below to save the file.`);
    } catch (error) {
      console.error("Create backup error:", error);
      toast.error("Failed to create backup");
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreDatabase = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error("Please select a JSON backup file");
      return;
    }
    
    // Confirm restore action
    if (!window.confirm("Are you sure you want to restore from this backup? This will add any missing records from the backup file. Existing records will NOT be overwritten.")) {
      event.target.value = '';
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/restore-database`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { results } = response.data;
      
      toast.success(
        `Restore complete! Equipment: ${results.equipment_restored} added, ${results.equipment_skipped} skipped. ` +
        `Requests: ${results.requests_restored} added. Types: ${results.equipment_types_restored} added.`
      );
      
      if (results.errors && results.errors.length > 0) {
        console.error("Restore errors:", results.errors);
        toast.error(`${results.errors.length} errors occurred. Check console for details.`);
      }
      
      // Refresh data
      fetchData();
      fetchAnalytics();
    } catch (error) {
      console.error("Restore error:", error);
      toast.error(error.response?.data?.detail || "Failed to restore database");
    }
    
    // Reset file input
    event.target.value = '';
  };

  // Run category migration
  const handleRunMigration = async () => {
    if (!window.confirm("This will update all uncategorized equipment with categories based on their equipment type. Continue?")) {
      return;
    }
    
    try {
      toast.info("Running category migration...");
      const response = await axios.post(`${API}/migrate-categories`);
      
      const { total_uncategorized, updated, unmatched } = response.data;
      
      if (updated > 0) {
        toast.success(`Migration complete! ${updated} of ${total_uncategorized} equipment updated.`);
      } else if (total_uncategorized === 0) {
        toast.info("All equipment already has categories assigned.");
      } else {
        toast.warning(`Found ${total_uncategorized} uncategorized items, but couldn't match ${total_uncategorized - updated}. You may need to update them manually.`);
      }
      
      if (unmatched && unmatched.length > 0) {
        console.log("Unmatched equipment types:", unmatched);
        toast.info(`${unmatched.length} items couldn't be matched. Check console for details.`);
      }
      
      // Refresh data
      fetchData();
      fetchAnalytics();
    } catch (error) {
      console.error("Migration error:", error);
      toast.error("Failed to run migration");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getRequestStatusBadge = (status) => {
    const styles = {
      Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Approved: "bg-green-100 text-green-800 border-green-200",
      Rejected: "bg-red-100 text-red-800 border-red-200",
      Completed: "bg-blue-100 text-blue-800 border-blue-200"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const getEquipmentForRequest = (equipmentId) => {
    return equipment.find(eq => eq.id === equipmentId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-slate-800 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/")}
                className="text-white hover:bg-slate-700"
                data-testid="back-to-site-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Site
              </Button>
              <div className="border-l border-slate-600 pl-4">
                <h1 className="text-xl font-bold">Admin Dashboard</h1>
                <span className="text-sm text-slate-400">Regency Cares Management</span>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={() => {
                localStorage.removeItem("isAdmin");
                navigate("/");
                toast.success("Logged out successfully");
              }}
              className="text-white hover:bg-slate-700"
              data-testid="dashboard-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-50" data-testid="tab-overview">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="equipment" className="data-[state=active]:bg-blue-50" data-testid="tab-equipment">
              <Package className="w-4 h-4 mr-2" />
              Equipment Admin ({equipment.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-blue-50" data-testid="tab-requests">
              <MessageSquare className="w-4 h-4 mr-2" />
              Requests ({requests.length})
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-blue-50" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="types" className="data-[state=active]:bg-blue-50" data-testid="tab-types">
              <Settings className="w-4 h-4 mr-2" />
              Equipment Types ({equipmentTypesList.length})
            </TabsTrigger>
            <TabsTrigger value="admins" className="data-[state=active]:bg-blue-50" data-testid="tab-admins">
              <Users className="w-4 h-4 mr-2" />
              Admin Users ({adminUsers.length || 1})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                title="Total Equipment" 
                value={stats.total_equipment} 
                icon={Package} 
                color="blue"
                subtext="All listings"
              />
              <StatCard 
                title="Available" 
                value={stats.available_equipment} 
                icon={CheckCircle} 
                color="green"
                subtext="Ready to lend"
              />
              <StatCard 
                title="Total Requests" 
                value={stats.total_requests} 
                icon={MessageSquare} 
                color="purple"
                subtext="All time"
              />
              <StatCard 
                title="Pending Requests" 
                value={stats.pending_requests} 
                icon={Clock} 
                color="yellow"
                subtext="Awaiting action"
              />
            </div>
            
            {/* Visitor and Admin Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard 
                title="Page Visitors" 
                value={stats.unique_visitors || 0} 
                icon={Eye} 
                color="blue"
                subtext="Unique visitors"
              />
              <StatCard 
                title="Total Page Views" 
                value={stats.total_visitors || 0} 
                icon={Eye} 
                color="green"
                subtext="All time views"
              />
              <StatCard 
                title="Admin Users" 
                value={stats.admin_count || 1} 
                icon={Users} 
                color="purple"
                subtext="With dashboard access"
              />
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-600" />
                    Pending Requests
                  </CardTitle>
                  <CardDescription>Requests awaiting your approval</CardDescription>
                </CardHeader>
                <CardContent>
                  {requests.filter(r => r.status === "Pending").length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No pending requests</p>
                  ) : (
                    <div className="space-y-3">
                      {requests.filter(r => r.status === "Pending").slice(0, 5).map((req) => {
                        const eq = getEquipmentForRequest(req.equipment_id);
                        return (
                          <div key={req.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium">{req.requester_name}</p>
                              <p className="text-sm text-gray-500">
                                Requesting: {eq?.equipment_type || "Unknown"}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleUpdateRequestStatus(req.id, "Approved")}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleUpdateRequestStatus(req.id, "Rejected")}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    Recent Equipment
                  </CardTitle>
                  <CardDescription>Latest equipment listings</CardDescription>
                </CardHeader>
                <CardContent>
                  {equipment.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No equipment listed</p>
                  ) : (
                    <div className="space-y-3">
                      {equipment.slice(0, 5).map((eq) => (
                        <div key={eq.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{eq.equipment_type}</p>
                            <p className="text-sm text-gray-500">
                              {eq.provider_name} • {eq.zip_code}
                            </p>
                          </div>
                          <Badge className={`${
                            eq.availability_status === "Available" ? "bg-green-100 text-green-800" :
                            eq.availability_status === "On Loan" ? "bg-yellow-100 text-yellow-800" :
                            "bg-red-100 text-red-800"
                          }`}>
                            {eq.availability_status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Equipment Tab */}
          <TabsContent value="equipment">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Equipment Admin</CardTitle>
                  <CardDescription>Manage all equipment listings</CardDescription>
                </div>
                <div className="flex gap-2">
                  {/* Manage Categories Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowCategoryManager(!showCategoryManager)}
                    className={`border-amber-500 text-amber-600 hover:bg-amber-50 ${showCategoryManager ? 'bg-amber-50' : ''}`}
                    data-testid="manage-categories-btn"
                  >
                    <Layers className="w-4 h-4 mr-2" />
                    Manage Categories
                  </Button>
                  {/* CSV Export Button */}
                  <Button 
                    variant="outline" 
                    onClick={handleExportCSV}
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  
                  {/* CSV Import Button */}
                  <label>
                    <Button 
                      variant="outline" 
                      className="border-blue-500 text-blue-600 hover:bg-blue-50 cursor-pointer"
                      asChild
                    >
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Import CSV
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleImportCSV}
                      className="hidden"
                      data-testid="import-csv-input"
                    />
                  </label>
                  
                  <Button onClick={() => navigate("/offer")} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Equipment
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Category Manager Panel */}
                {showCategoryManager && (
                  <div className="mb-6 border-2 border-amber-200 rounded-lg bg-amber-50 p-4" data-testid="category-manager-panel">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Layers className="w-5 h-5 text-amber-600" />
                      Category & Subcategory Manager
                    </h3>

                    {/* Add New Category */}
                    <div className="flex gap-2 mb-4 p-3 bg-white rounded-lg border">
                      <Input
                        placeholder="New category name..."
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        className="max-w-xs"
                        data-testid="new-category-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                      />
                      <Button onClick={handleAddCategory} className="bg-amber-600 hover:bg-amber-700" data-testid="add-category-btn">
                        <FolderPlus className="w-4 h-4 mr-2" />
                        Add Category
                      </Button>
                    </div>

                    {/* Add Subcategory to Existing Category */}
                    <div className="flex gap-2 mb-4 p-3 bg-white rounded-lg border">
                      <Select value={selectedCategoryForSub} onValueChange={setSelectedCategoryForSub}>
                        <SelectTrigger className="w-[220px]" data-testid="select-category-for-sub">
                          <SelectValue placeholder="Select category..." />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(adminCategories).sort().map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="New subcategory name..."
                        value={newSubcategoryName}
                        onChange={(e) => setNewSubcategoryName(e.target.value)}
                        className="max-w-xs"
                        data-testid="new-subcategory-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddSubcategory()}
                      />
                      <Button onClick={handleAddSubcategory} className="bg-blue-600 hover:bg-blue-700" data-testid="add-subcategory-btn">
                        <PlusCircle className="w-4 h-4 mr-2" />
                        Add Subcategory
                      </Button>
                    </div>

                    {/* Category List */}
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {Object.entries(adminCategories).sort(([a],[b]) => a.localeCompare(b)).map(([catName, subcats]) => (
                        <div key={catName} className="bg-white rounded-lg border p-3" data-testid={`category-item-${catName}`}>
                          {/* Category Header */}
                          <div className="flex items-center justify-between mb-2">
                            {editingCategory === catName ? (
                              <div className="flex items-center gap-2 flex-1">
                                <Input
                                  value={editCategoryName}
                                  onChange={(e) => setEditCategoryName(e.target.value)}
                                  className="max-w-xs text-sm"
                                  data-testid={`edit-category-input-${catName}`}
                                  onKeyDown={(e) => e.key === 'Enter' && handleRenameCategory(catName)}
                                  autoFocus
                                />
                                <Button size="sm" variant="ghost" onClick={() => handleRenameCategory(catName)} data-testid={`save-category-btn-${catName}`}>
                                  <Check className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingCategory(null)}>
                                  <X className="w-4 h-4 text-gray-400" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{catName}</span>
                                <Badge variant="secondary" className="text-xs">{subcats.length} subcategories</Badge>
                              </div>
                            )}
                            {editingCategory !== catName && (
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="ghost" onClick={() => { setEditingCategory(catName); setEditCategoryName(catName); }} data-testid={`rename-category-btn-${catName}`}>
                                  <Pencil className="w-3.5 h-3.5 text-blue-500" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteCategory(catName)} data-testid={`delete-category-btn-${catName}`}>
                                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                </Button>
                              </div>
                            )}
                          </div>
                          {/* Subcategories */}
                          <div className="flex flex-wrap gap-2">
                            {subcats.map((sub) => (
                              <div key={sub} className="flex items-center gap-1 bg-gray-50 border rounded px-2 py-1 text-sm" data-testid={`subcategory-item-${sub}`}>
                                {editingSubcategory === `${catName}::${sub}` ? (
                                  <>
                                    <Input
                                      value={editSubcategoryName}
                                      onChange={(e) => setEditSubcategoryName(e.target.value)}
                                      className="h-6 w-40 text-xs px-1"
                                      data-testid={`edit-subcategory-input-${sub}`}
                                      onKeyDown={(e) => e.key === 'Enter' && handleRenameSubcategory(catName, sub)}
                                      autoFocus
                                    />
                                    <button onClick={() => handleRenameSubcategory(catName, sub)} className="text-green-600 hover:text-green-800">
                                      <Check className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => setEditingSubcategory(null)} className="text-gray-400 hover:text-gray-600">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-gray-700">{sub}</span>
                                    <button onClick={() => { setEditingSubcategory(`${catName}::${sub}`); setEditSubcategoryName(sub); }} className="text-blue-400 hover:text-blue-600 ml-1" data-testid={`rename-sub-btn-${sub}`}>
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => handleDeleteSubcategory(catName, sub)} className="text-red-400 hover:text-red-600" data-testid={`delete-sub-btn-${sub}`}>
                                      <X className="w-3 h-3" />
                                    </button>
                                  </>
                                )}
                              </div>
                            ))}
                            {subcats.length === 0 && (
                              <span className="text-sm text-gray-400 italic">No subcategories yet</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Equipment</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Zip Code</TableHead>
                      <TableHead>Added On</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Requests</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {equipment.map((eq) => (
                      <TableRow key={eq.id} data-testid={`admin-equipment-row-${eq.id}`}>
                        <TableCell>
                          <div className="font-medium">{eq.equipment_type}</div>
                          <div className="text-sm text-gray-500 truncate max-w-[200px]">{eq.description}</div>
                        </TableCell>
                        <TableCell>
                          <div>{eq.provider_name}</div>
                          <div className="text-sm text-gray-500">{eq.email}</div>
                        </TableCell>
                        <TableCell>{eq.zip_code}</TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-600">{formatDate(eq.created_at)}</div>
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={eq.availability_status} 
                            onValueChange={(value) => handleUpdateEquipmentStatus(eq.id, value)}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {AVAILABILITY_STATUSES.map((status) => (
                                <SelectItem key={status} value={status}>{status}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {eq.pending_request_count} pending
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditEquipment(eq)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteEquipment(eq.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests">
            <Card>
              <CardHeader>
                <CardTitle>Request Management</CardTitle>
                <CardDescription>View and manage all equipment borrow requests</CardDescription>
              </CardHeader>
              <CardContent>
                {requests.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No requests yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Requester</TableHead>
                        <TableHead>Equipment</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {requests.map((req) => {
                        const eq = getEquipmentForRequest(req.equipment_id);
                        return (
                          <TableRow key={req.id} data-testid={`admin-request-row-${req.id}`}>
                            <TableCell>
                              <div className="font-medium">{req.requester_name}</div>
                              <div className="text-sm text-gray-500">
                                {new Date(req.created_at).toLocaleDateString()}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{eq?.equipment_type || "Unknown"}</div>
                              <div className="text-sm text-gray-500">{eq?.provider_name || "-"}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <a href={`mailto:${req.requester_email}`} className="text-blue-600 hover:underline block">
                                  {req.requester_email}
                                </a>
                                <a href={`tel:${req.requester_phone}`} className="text-blue-600 hover:underline">
                                  {req.requester_phone}
                                </a>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="max-w-[200px] truncate text-sm text-gray-600">
                                {req.message || "-"}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={getRequestStatusBadge(req.status)}>
                                {req.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {req.status === "Pending" && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0"
                                      onClick={() => handleUpdateRequestStatus(req.id, "Approved")}
                                      title="Approve"
                                    >
                                      <Check className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="destructive"
                                      className="h-8 w-8 p-0"
                                      onClick={() => handleUpdateRequestStatus(req.id, "Rejected")}
                                      title="Reject"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {req.status === "Approved" && (
                                  <Button 
                                    size="sm" 
                                    className="bg-blue-600 hover:bg-blue-700 h-8"
                                    onClick={() => handleUpdateRequestStatus(req.id, "Completed")}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Complete
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                  onClick={() => handleDeleteRequest(req.id)}
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
                  <p className="text-gray-500">Track trends, popular equipment, and visitor engagement</p>
                </div>
              </div>

              {/* Top Row - Pie Charts */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Equipment by Category */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-blue-500" />
                      Equipment by Category
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analyticsData.equipmentByCategory.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <RechartsPie>
                          <Pie
                            data={analyticsData.equipmentByCategory}
                            dataKey="count"
                            nameKey="category"
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            label={({ category, count }) => `${count}`}
                          >
                            {analyticsData.equipmentByCategory.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPie>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-gray-400">No data available</div>
                    )}
                  </CardContent>
                </Card>

                {/* Equipment by Status */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      Equipment Availability
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analyticsData.equipmentByStatus.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <RechartsPie>
                          <Pie
                            data={analyticsData.equipmentByStatus}
                            dataKey="count"
                            nameKey="status"
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            label={({ status, count }) => `${status}: ${count}`}
                          >
                            {analyticsData.equipmentByStatus.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.status === 'Available' ? '#10b981' : entry.status === 'On Loan' ? '#f59e0b' : '#ef4444'} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPie>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-gray-400">No data available</div>
                    )}
                  </CardContent>
                </Card>

                {/* Requests by Status */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-purple-500" />
                      Requests by Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analyticsData.requestsByStatus.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <RechartsPie>
                          <Pie
                            data={analyticsData.requestsByStatus}
                            dataKey="count"
                            nameKey="status"
                            cx="50%"
                            cy="50%"
                            outerRadius={70}
                            label={({ status, count }) => `${status}: ${count}`}
                          >
                            {analyticsData.requestsByStatus.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={
                                entry.status === 'Approved' ? '#10b981' : 
                                entry.status === 'Pending' ? '#f59e0b' : 
                                entry.status === 'Rejected' ? '#ef4444' : '#3b82f6'
                              } />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPie>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-gray-400">No data available</div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Middle Row - Line Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Page Visits Over Time */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                      Page Visits (Last 30 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analyticsData.visitsOverTime.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={analyticsData.visitsOverTime}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="visits" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-gray-400">No visit data yet</div>
                    )}
                  </CardContent>
                </Card>

                {/* Requests Over Time */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-green-500" />
                      Equipment Requests (Last 30 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analyticsData.requestsOverTime.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={analyticsData.requestsOverTime}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="requests" fill="#10b981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-gray-400">No request data yet</div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Bottom Row - Popular Equipment */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-500" />
                    Most Requested Equipment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analyticsData.popularEquipment.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analyticsData.popularEquipment} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="requests" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-400">
                      No equipment requests yet. Data will appear as users request equipment.
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Category Migration Section */}
              <Card className="mt-6 border-2 border-amber-200 bg-amber-50" data-testid="category-migration-section">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="w-5 h-5 text-amber-600" />
                    Category Migration Tool
                  </CardTitle>
                  <CardDescription>Automatically assign categories to equipment that only has legacy equipment types</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">
                        This tool scans all equipment records without a category and attempts to map them 
                        using their equipment type field. Items that can't be automatically matched will be 
                        listed so you can update them manually.
                      </p>
                    </div>
                    <Button
                      onClick={handleRunMigration}
                      className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
                      data-testid="run-migration-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Run Category Migration
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Database Backup Section */}
              <Card className="mt-6 border-2 border-purple-200 bg-purple-50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Database className="w-5 h-5 text-purple-600" />
                    Database Backup & Restore
                  </CardTitle>
                  <CardDescription>Create a backup, download it, or restore from a previous backup file</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Step 1: Create Backup */}
                    <div className="p-4 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">Step 1</span>
                        <h4 className="font-semibold text-gray-900">Create Backup</h4>
                      </div>
                      <p className="text-sm text-gray-500 mb-4">
                        Generate a new backup of all your data.
                      </p>
                      <Button 
                        onClick={handleCreateBackup}
                        disabled={backupLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        data-testid="create-backup-btn"
                      >
                        {backupLoading ? (
                          <>Creating...</>
                        ) : (
                          <>
                            <Database className="w-4 h-4 mr-2" />
                            Create Backup
                          </>
                        )}
                      </Button>
                      {backupStatus.available && (
                        <p className="text-xs text-green-600 mt-2">
                          Last backup: {backupStatus.filename}
                        </p>
                      )}
                    </div>
                    
                    {/* Step 2: Download Backup */}
                    <div className="p-4 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded">Step 2</span>
                        <h4 className="font-semibold text-gray-900">Download Backup</h4>
                      </div>
                      <p className="text-sm text-gray-500 mb-4">
                        {backupDownloadUrl ? "Right-click the link and select 'Save link as...'" : "Create a backup first, then download."}
                      </p>
                      {backupDownloadUrl ? (
                        <a
                          href={backupDownloadUrl}
                          download={backupStatus.filename}
                          className="flex items-center justify-center w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md font-medium"
                          data-testid="download-backup-link"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Save: {backupStatus.filename}
                        </a>
                      ) : (
                        <Button 
                          disabled
                          className="w-full bg-purple-600 opacity-50 cursor-not-allowed"
                          data-testid="download-backup-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Backup
                        </Button>
                      )}
                      {!backupDownloadUrl && (
                        <p className="text-xs text-gray-400 mt-2">
                          Create a backup first
                        </p>
                      )}
                    </div>
                    
                    {/* Restore Backup */}
                    <div className="p-4 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">Restore</span>
                        <h4 className="font-semibold text-gray-900">Restore from Backup</h4>
                      </div>
                      <p className="text-sm text-gray-500 mb-4">
                        Upload a backup file to restore.
                      </p>
                      <label className="block">
                        <Button 
                          variant="outline"
                          className="w-full border-orange-500 text-orange-600 hover:bg-orange-50 cursor-pointer"
                          asChild
                        >
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload & Restore
                          </span>
                        </Button>
                        <input
                          type="file"
                          accept=".json"
                          onChange={handleRestoreDatabase}
                          className="hidden"
                          data-testid="restore-backup-input"
                        />
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Equipment Types Tab */}
          <TabsContent value="types">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Equipment Types Management
                </CardTitle>
                <CardDescription>Add, view, or remove equipment types available in the system</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Add New Type */}
                <div className="flex gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
                  <Input
                    placeholder="Enter new equipment type name..."
                    value={newTypeName}
                    onChange={(e) => setNewTypeName(e.target.value)}
                    className="max-w-sm"
                    data-testid="new-type-input"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddEquipmentType()}
                  />
                  <Button 
                    onClick={handleAddEquipmentType}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="add-type-btn"
                  >
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Add Type
                  </Button>
                </div>

                {/* Types List */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {equipmentTypesList.map((type) => {
                    const usageCount = equipment.filter(eq => eq.equipment_type === type.name).length;
                    return (
                      <div 
                        key={type.id} 
                        className="flex items-center justify-between p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                        data-testid={`type-item-${type.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Package className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{type.name}</p>
                            <p className="text-xs text-gray-500">{usageCount} item{usageCount !== 1 ? 's' : ''} using this type</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteEquipmentType(type.id, type.name)}
                          disabled={usageCount > 0}
                          title={usageCount > 0 ? "Cannot delete: type is in use" : "Delete type"}
                          data-testid={`delete-type-${type.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>

                {equipmentTypesList.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No equipment types defined. Add one above!</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin Users Tab */}
          <TabsContent value="admins">
            <Card>
              <CardHeader>
                <CardTitle>Admin User Management</CardTitle>
                <CardDescription>Manage users who have access to this admin dashboard</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Add New Admin Form */}
                <div className="flex gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
                  <Input
                    placeholder="Username"
                    value={newAdminUsername}
                    onChange={(e) => setNewAdminUsername(e.target.value)}
                    className="flex-1"
                    data-testid="new-admin-username"
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={newAdminPassword}
                    onChange={(e) => setNewAdminPassword(e.target.value)}
                    className="flex-1"
                    data-testid="new-admin-password"
                  />
                  <Button onClick={handleAddAdminUser} className="bg-blue-600 hover:bg-blue-700">
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Add Admin
                  </Button>
                </div>

                {/* Admin Users List */}
                <div className="space-y-3">
                  {/* Default Admin (from env) */}
                  <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Default Admin</p>
                        <p className="text-xs text-gray-500">System admin (cannot be deleted)</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700">Active</Badge>
                  </div>

                  {/* Database Admin Users */}
                  {adminUsers.map((user) => (
                    <div 
                      key={user.id} 
                      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                      data-testid={`admin-user-${user.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.username}</p>
                          <p className="text-xs text-gray-500">Added {formatDate(user.created_at)}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteAdminUser(user.id, user.username)}
                        data-testid={`delete-admin-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {adminUsers.length === 0 && (
                  <p className="text-gray-500 text-center py-4 mt-4">No additional admin users. Only the default admin has access.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categories, setCategories] = useState({});
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const fetchEquipment = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (selectedCategory !== "All") params.append("category", selectedCategory);
      
      const response = await axios.get(`${API}/equipment?${params.toString()}`);
      setEquipment(response.data);
    } catch (error) {
      console.error("Error fetching equipment:", error);
      toast.error("Failed to load equipment");
    } finally {
      setLoading(false);
    }
  }, [search, selectedCategory]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  useEffect(() => {
    const adminStatus = localStorage.getItem("isAdmin");
    if (adminStatus === "true") {
      setIsAdmin(true);
    }
    
    // Track page visit
    const trackVisit = async () => {
      try {
        // Generate or get visitor ID from localStorage
        let visitorId = localStorage.getItem("visitorId");
        if (!visitorId) {
          visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem("visitorId", visitorId);
        }
        await axios.post(`${API}/track-visit?page=medical_library&visitor_id=${visitorId}`);
      } catch (error) {
        console.log("Visit tracking failed:", error);
      }
    };
    trackVisit();
  }, []);

  const handleRequestSubmit = async (equipmentId, formData) => {
    try {
      await axios.post(`${API}/equipment/${equipmentId}/request`, formData);
      toast.success("Request sent successfully!");
      fetchEquipment();
    } catch (error) {
      toast.error("Failed to send request");
      throw error;
    }
  };

  const handleAdminLogin = async (password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAdmin(true);
        localStorage.setItem("isAdmin", "true");
        toast.success("Admin login successful!");
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    localStorage.removeItem("isAdmin");
    toast.success("Logged out successfully");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this equipment?")) return;
    try {
      await axios.delete(`${API}/equipment/${id}`);
      toast.success("Equipment deleted successfully");
      fetchEquipment();
    } catch (error) {
      toast.error("Failed to delete equipment");
    }
  };

  const handleEdit = (equipment) => {
    navigate("/offer", { state: { equipment } });
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#dce3ed'}}>
      <Header 
        onOfferClick={() => navigate("/offer")} 
        onAdminClick={() => setShowAdminModal(true)}
        isAdmin={isAdmin}
        onLogout={handleLogout}
        onDashboardClick={() => navigate("/admin")}
      />
      
      {/* Hero Section - Reduced height, light blue-gray background */}
      <div 
        className="py-8"
        style={{
          backgroundColor: '#dce3ed'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-gray-700 mb-2">
            Medical Equipment <span className="text-pink-600 font-medium">Lending Library</span>
          </h2>
          <p className="text-base text-gray-600 mb-4 max-w-2xl mx-auto">
            Browse available medical equipment shared by your Regency neighbors. 
            Together, we're building a caring and connected community.
          </p>
          <div className="flex justify-center gap-4">
            <Button 
              onClick={() => navigate("/offer")}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md shadow-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Offer Equipment
            </Button>
            <Button 
              onClick={() => window.location.href = 'https://regencycares.com/medical-library'}
              variant="outline"
              className="border-2 border-gray-400 text-gray-700 hover:bg-gray-200 px-6 py-2 rounded-md"
              style={{backgroundColor: '#dce3ed'}}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Main Site
            </Button>
          </div>
        </div>
      </div>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" style={{backgroundColor: '#dce3ed'}}>
        {/* Section Title */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Available Equipment</h3>
          <div className="w-24 h-1 bg-pink-500 mx-auto rounded-full"></div>
        </div>
        
        {/* Search and Filter */}
        <SearchFilter
          search={search}
          setSearch={setSearch}
          category={selectedCategory}
          setCategory={setSelectedCategory}
          categories={categories}
        />

        {/* Equipment Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading equipment...</p>
          </div>
        ) : (
          <EquipmentTable
            equipment={equipment}
            onRequestClick={(eq) => {
              setSelectedEquipment(eq);
              setShowRequestModal(true);
            }}
          />
        )}
        
        {/* Footer Mission */}
        <div className="mt-8 p-6 rounded-xl border border-gray-300 shadow-sm" style={{backgroundColor: '#d0dae8'}}>
          <h3 className="font-bold text-lg text-gray-800 mb-2">Our Mission</h3>
          <p className="text-gray-600 text-sm">
            At Regency Cares, our mission is to foster a caring and connected community. 
            We believe that everyone deserves access to support and resources, and we are 
            dedicated to making that a reality for our neighbors.
          </p>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-700 text-white py-6 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-300">
            © 2026 Regency Cares. All rights reserved. | 
            <a href="https://regencycares.com" className="hover:text-pink-300 ml-1">regencycares.com</a>
          </p>
        </div>
      </footer>

      {/* Modals */}
      <RequestModal
        isOpen={showRequestModal}
        onClose={() => {
          setShowRequestModal(false);
          setSelectedEquipment(null);
        }}
        equipment={selectedEquipment}
        onSubmit={handleRequestSubmit}
      />

      <AdminLoginModal
        isOpen={showAdminModal}
        onClose={() => setShowAdminModal(false)}
        onLogin={handleAdminLogin}
      />
    </div>
  );
};

// Offer Equipment Page
const OfferEquipmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editingEquipment = location.state?.equipment;
  const isEditing = !!editingEquipment;
  const [categories, setCategories] = useState({});

  const [formData, setFormData] = useState({
    provider_name: editingEquipment?.provider_name || "",
    full_address: editingEquipment?.full_address || "",
    zip_code: editingEquipment?.zip_code || "",
    phone_number: editingEquipment?.phone_number || "",
    email: editingEquipment?.email || "",
    category: editingEquipment?.category || "",
    subcategory: editingEquipment?.subcategory || "",
    equipment_type: editingEquipment?.equipment_type || "",
    availability_status: editingEquipment?.availability_status || "Available",
    description: editingEquipment?.description || "",
    special_notes: editingEquipment?.special_notes || ""
  });
  const [loading, setLoading] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Get subcategories for selected category
  const getSubcategories = () => {
    if (!formData.category || !categories[formData.category]) {
      return [];
    }
    return categories[formData.category];
  };

  // Handle category change - reset subcategory
  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value, subcategory: "" });
  };

  // Phone number formatting helper
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as xxx.xxx.xxxx
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone_number: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.category) {
      toast.error("Please select a Category");
      return;
    }
    if (!formData.subcategory) {
      toast.error("Please select a Subcategory");
      return;
    }
    
    setLoading(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/equipment/${editingEquipment.id}`, formData);
        toast.success("Equipment updated successfully!");
      } else {
        await axios.post(`${API}/equipment`, formData);
        toast.success("Equipment listed successfully!");
      }
      navigate("/");
    } catch (error) {
      console.error("Error saving equipment:", error);
      toast.error(isEditing ? "Failed to update equipment" : "Failed to list equipment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #4a4a4a 0%, #2d2d2d 100%)'}}>
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-800 to-gray-900 border-b border-pink-500/30 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-1"></div>
            <div className="flex flex-col items-center">
              <h1 className="text-xl font-bold text-white">Regency <span className="text-pink-400">Cares</span></h1>
              <span className="text-sm text-gray-400">Offer Equipment</span>
            </div>
            <div className="flex-1 flex justify-end">
              <Button 
                variant="outline" 
                onClick={() => navigate("/")}
                className="bg-transparent border-pink-400/50 text-pink-300 hover:bg-pink-500/20"
                data-testid="back-to-home-btn"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-gray-900/90 border-pink-500/30 shadow-xl" data-testid="offer-form-card">
          <CardHeader className="border-b border-pink-500/20">
            <CardTitle className="text-2xl text-white">
              {isEditing ? "Edit Equipment" : "Share Your Medical Equipment"}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {isEditing ? "Update the equipment details below." : "Help your Regency neighbors by offering equipment you can loan out."}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-pink-300 border-b border-pink-500/30 pb-2">
                    Contact Information
                  </h3>
                  
                  {/* Date Submitted - Auto-populated */}
                  <div>
                    <Label htmlFor="date_submitted" className="text-gray-300">Date Submitted</Label>
                    <Input
                      id="date_submitted"
                      value={isEditing && editingEquipment?.created_at 
                        ? new Date(editingEquipment.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                        : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                      }
                      disabled
                      className="bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed"
                      data-testid="date-submitted-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="provider_name" className="text-gray-300">Provider Name</Label>
                    <Input
                      id="provider_name"
                      placeholder="Your full name"
                      value={formData.provider_name}
                      onChange={(e) => setFormData({ ...formData, provider_name: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-600 text-white placeholder:text-gray-500 focus:border-pink-400"
                      data-testid="provider-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="full_address" className="text-slate-300">Full Address</Label>
                    <Input
                      id="full_address"
                      placeholder="123 Main Street, Apt 4B"
                      value={formData.full_address}
                      onChange={(e) => setFormData({ ...formData, full_address: e.target.value })}
                      required
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="address-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="zip_code" className="text-slate-300">Zip Code</Label>
                    <Input
                      id="zip_code"
                      placeholder="e.g., 12345"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      required
                      maxLength={10}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="zip-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone_number" className="text-slate-300">Phone Number</Label>
                    <Input
                      id="phone_number"
                      placeholder="xxx.xxx.xxxx"
                      value={formData.phone_number}
                      onChange={handlePhoneChange}
                      required
                      maxLength={12}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-slate-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="email-input"
                    />
                  </div>
                </div>

                {/* Equipment Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-pink-300 border-b border-pink-500/30 pb-2">
                    Equipment Details
                  </h3>
                  
                  {/* Category Selection */}
                  <div>
                    <Label htmlFor="category" className="text-slate-300">Category <span className="text-red-400">*</span></Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={handleCategoryChange}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(categories).sort().map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subcategory Selection - depends on Category */}
                  <div>
                    <Label htmlFor="subcategory" className="text-slate-300">Subcategory (Equipment Type) <span className="text-red-400">*</span></Label>
                    <Select 
                      value={formData.subcategory} 
                      onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                      disabled={!formData.category}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-600 text-white disabled:opacity-50" data-testid="subcategory-select">
                        <SelectValue placeholder={formData.category ? "Select subcategory" : "Select category first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {getSubcategories().map((subcat) => (
                          <SelectItem key={subcat} value={subcat}>{subcat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Availability Status hidden - defaults to "Available" */}

                  <div>
                    <Label htmlFor="description" className="text-slate-300">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Describe the equipment, its condition, size, etc."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 min-h-[100px]"
                      data-testid="description-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="special_notes" className="text-slate-300">Special Notes (Optional)</Label>
                    <Textarea
                      id="special_notes"
                      placeholder="Any special instructions, requirements, or restrictions"
                      value={formData.special_notes}
                      onChange={(e) => setFormData({ ...formData, special_notes: e.target.value })}
                      className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                      data-testid="notes-input"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate("/")}
                  className="bg-transparent border-slate-600 text-white hover:bg-slate-700"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-equipment-btn"
                >
                  {loading ? "Saving..." : isEditing ? "Update Equipment" : "List Equipment"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/offer" element={<OfferEquipmentPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
